import React, { useState } from "react";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useContextSelector } from "use-context-selector";
import { useTranslation } from "react-i18next";
import Layout from "../Layout";
import AuthContext from "../../context/AuthContext";
import { apiPost } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";

// Old design showed a fully masked number ("+880 ******6110"); phone is
// stored as a single string with no separate country-code field, so this
// assumes the first 3 digits are the country code. Adjust if your phone
// values are shaped differently.
const maskPhone = (phone) => {
  if (!phone) return "-";
  const digits = phone.toString();
  if (digits.length <= 7) return `+${digits}`;
  const countryCode = digits.slice(0, 3);
  const last4 = digits.slice(-4);
  const maskLength = digits.length - 3 - 4;
  return `+${countryCode} ${"*".repeat(Math.max(maskLength, 0))}${last4}`;
};

// Same validation rules AddProfile.jsx used to run through react-hook-form,
// reproduced as plain functions since this dialog no longer uses that lib.
const validateField = (t, type, value) => {
  const v = (value || "").toString();
  if (type === "birthday") {
    if (!v) return t("DateOfBirthIsRequired");
    const today = new Date().toISOString().split("T")[0];
    if (v > today) return t("DateMustBeInThePast");
    return "";
  }
  if (type === "fullname") {
    if (!v.trim()) return t("EnterAValidUsername");
    if (/^\s/.test(v) || /\s$/.test(v)) return t("EnterAValidUsername");
    if (!/^[a-zA-Z0-9 ]*$/.test(v)) return t("OnlyAlphabetsAndNumbersAllowed");
    if (!/[a-zA-Z]/.test(v)) return t("UsernameMustContainAtLeastOneLetter");
    if (v.trim().length < 3) return t("Minimum3Characters");
    if (v.trim().length > 30) return t("Maximum30Characters");
    return "";
  }
  // email
  if (!v) return t("Pleaseenteremail");
  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v))
    return t("Invalidemailaddress");
  return "";
};

const ProfileAccount = () => {
  const { t } = useTranslation();
  const profileData = useContextSelector(AuthContext, (ctx) => {
    return ctx.profileData;
  });
  const getProfileData = useContextSelector(AuthContext, (ctx) => {
    return ctx.getProfileData;
  });

  const [select, setSelect] = useState({
    type: "",
    title: "",
    status: false,
  });

  // -- phone flow (password-based, matches the reference AddProfile.jsx —
  // no OTP request/captcha, just phone + password + Submit) --
  const [mobileInput, setMobileInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);

  // -- fullname / birthday / email flow (replaces AddProfile.jsx) --
  const [fieldValue, setFieldValue] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [fieldLoading, setFieldLoading] = useState(false);

  const isDialogOpen = select?.status;
  const isPhoneDialog = isDialogOpen && select?.type === "phone";
  const isFieldDialog =
    isDialogOpen && ["fullname", "birthday", "email"].includes(select?.type);

  const closeDialog = () => {
    setSelect({});
    setMobileInput("");
    setPasswordInput("");
    setPhoneError("");
    setFieldValue("");
    setFieldError("");
  };

  const handleSubmitPhone = async () => {
    if (select?.statusPhone && !mobileInput) {
      setPhoneError(t("PleaseEnterYourPhoneNumber"));
      return;
    }
    if (!passwordInput) {
      setPhoneError(t("PleaseEnterPassword"));
      return;
    }
    setPhoneError("");
    setPhoneLoading(true);

    const body = select?.statusPhone
      ? {
          new_phone_number: mobileInput,
          phone_number: profileData?.phone,
          type: "password",
          password: passwordInput,
        }
      : {
          phone_number: profileData?.phone,
          type: "password",
          password: passwordInput,
        };

    const { data } = await apiPost(apiPath.verifyOtpNew, body);

    if (data?.success) {
      toast.success(data?.message);
      getProfileData();
      closeDialog();
    } else {
      toast.error(data?.message);
    }
    setPhoneLoading(false);
  };

  const handleSubmitField = async () => {
    const error = validateField(t, select?.type, fieldValue);
    if (error) {
      setFieldError(error);
      return;
    }
    setFieldError("");
    setFieldLoading(true);

    // Same apiPost(apiPath.userProfileUpdate, ...) call AddProfile.jsx used
    // for the non-phone fields
    const { status, data: response } = await apiPost(
      apiPath.userProfileUpdate,
      select?.type === "birthday"
        ? { dob: fieldValue }
        : select?.type === "fullname"
          ? { fullName: fieldValue }
          : { email: fieldValue },
    );

    if (status === 200 && response?.success) {
      toast.success(response?.message);
      getProfileData();
      closeDialog();
    } else {
      toast.error(response?.message);
    }
    setFieldLoading(false);
  };

  return (
    <Layout>
      <div className="row no-gutters" style={{ backgroundColor: "white" }}>
        <div className="pa-2 col col-12">
          <div className="profile-container">
            <div className="profile-wrapper">
              <div className="avatar-wrapper">
                <div className="avatar" />
              </div>
              <div className="member-name mt-2">{profileData?.username}</div>
              <div className="profile-detail">
                <div className="no-gutters col">
                  <div className="field-name">{t("Username")}</div>
                  <div className="row no-gutters justify-space-between">
                    <div className="orange--text">
                      {profileData?.username}
                    </div>
                    {isEmpty(profileData?.firstName) && (
                      <button
                        type="button"
                        onClick={() =>
                          setSelect({
                            type: "fullname",
                            title: t("AddFullName"),
                            placeholder: t("YourFullNamePlaceholder"),
                            label: t("FullName"),
                            status: true,
                          })
                        }
                        className="v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--small primary"
                      >
                        <span className="v-btn__content">{t("Add")}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="no-gutters col">
                  <div className="field-name">{t("PhoneNumber")}</div>
                  <div className="row mobile-input no-gutters justify-space-between pa-0">
                    <div className="d-flex align-center justify-space-between remove-bord-btm col col-12">
                      <div className="orange--text">
                        {maskPhone(profileData?.phone)}
                      </div>
                      <div className="spacer" />
                      {!profileData?.isVerified && (
                        <div>
                          <label
                            onClick={() =>
                              setSelect({
                                type: "phone",
                                title: t("VerifyPhoneNumber"),
                                placeholder: t("YourPhoneNumberPlaceholder"),
                                label: t("PhoneNumber"),
                                status: true,
                                statusPhone: false,
                                phone: profileData?.phone,
                              })
                            }
                            className="px-2 warning-text d-flex"
                            style={{ cursor: "pointer" }}
                          >
                            {t("VerifyPhone")}
                            <div
                              className="v-image v-responsive ml-2 theme--light"
                              style={{ height: "25px", width: "25px" }}
                            >
                              <div
                                className="v-responsive__sizer"
                                style={{ paddingBottom: "100%" }}
                              />
                              <div
                                className="v-image__image v-image__image--contain"
                                style={{
                                  backgroundImage:
                                    'url("/static/image/profile/personal-info-warning.svg")',
                                  backgroundPosition: "center center",
                                }}
                              />
                              <div
                                className="v-responsive__content"
                                style={{ width: "19px" }}
                              />
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  {profileData?.isVerified && (
                    <div className="row">
                      <div className="pt-0 col col-12">
                        <button
                          type="button"
                          onClick={() =>
                            setSelect({
                              type: "phone",
                              title: t("AddPhoneNumberTitle"),
                              placeholder: t("AddNewPhoneNumberPlaceholder"),
                              label: t("PhoneNumber"),
                              status: true,
                              statusPhone: true,
                              phone: profileData?.phone,
                            })
                          }
                          className="v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--small primary"
                        >
                          <span className="v-btn__content">
                            {t("AddSecondaryNumber")}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="no-gutters col">
                  <div className="field-name">{t("Email")}</div>
                  <div className="row no-gutters justify-space-between">
                    <label className="orange--text">
                      {profileData?.email || "-"}
                    </label>
                    {isEmpty(profileData?.email) && (
                      <button
                        type="button"
                        onClick={() =>
                          setSelect({
                            type: "email",
                            title: t("AddEmailTitle"),
                            placeholder: t("YourEmailAddressPlaceholder"),
                            label: t("Email"),
                            status: true,
                          })
                        }
                        className="v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--small primary"
                      >
                        <span className="v-btn__content">{t("Add")}</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="no-gutters col">
                  <div className="field-name">{t("DateOfBirth")}</div>
                  <div className="row no-gutters justify-space-between">
                    <div className="orange--text">
                      {profileData?.dob || "-"}
                    </div>
                    {isEmpty(profileData?.dob) && (
                      <button
                        type="button"
                        onClick={() =>
                          setSelect({
                            type: "birthday",
                            title: t("AddBirthdayTitle"),
                            placeholder: t("YourBirthdayPlaceholder"),
                            label: t("Birthday"),
                            status: true,
                          })
                        }
                        className="v-btn v-btn--is-elevated v-btn--has-bg v-btn--rounded theme--light v-size--small primary"
                      >
                        <span className="v-btn__content">{t("Add")}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal — same v-dialog shell as before. Phone flow is now the
          reference AddProfile.jsx's password-based verification (no
          OTP request, no captcha) instead of the OTP flow. */}
      {isDialogOpen && (
        <div
          role="document"
          tabIndex={0}
          className="v-dialog__content v-dialog__content--active"
          style={{ zIndex: 202 }}
        >
          <div
            className="v-dialog v-dialog--active v-dialog--persistent"
            style={{ maxWidth: "400px" }}
          >
            <div className="dialog-card v-card v-sheet theme--light">
              <div className="row no-gutters justify-space-between dialog-header">
                <div className="d-flex flex-column pl-6 py-4 col col-10">
                  <label className="dialog-title text-capitalize d-block full-width white--text">
                    {select?.title}
                  </label>
                </div>
                <div className="d-flex justify-content-center align-content-center col col-2">
                  <button
                    type="button"
                    onClick={closeDialog}
                    aria-label={t("Close")}
                    className="v-icon notranslate full-width v-icon--link mdi mdi-close theme--light white--text dialog-close-icon"
                  />
                </div>
              </div>

              <div className="row dialog-row no-gutters">
                <form
                  noValidate
                  className="v-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  {isPhoneDialog && (
                    <>
                      <div className="otpPhoneNo pb-2 mobile">
                        <div className="row no-gutters justify-space-between">
                          <div className="col col-10">
                            <label
                              className="input-field-label ma-0 text-capitalize d-block"
                              style={{ float: "left" }}
                            >
                              {select?.statusPhone
                                ? t("NewPhoneNumber")
                                : t("PhoneNumber")}
                              <span className="red--text ml-1">*</span>
                            </label>
                          </div>
                          <div className="col col-2" />
                          <div className="col col-12">
                            <div
                              className={`v-input input-field elevation-0 hide-details theme--light v-text-field v-text-field--prefix v-text-field--is-booted v-text-field--enclosed v-text-field--outlined ${
                                select?.statusPhone
                                  ? ""
                                  : "v-input--is-disabled v-input--is-readonly"
                              }`}
                            >
                              <div className="v-input__control">
                                <div className="v-input__slot">
                                  <fieldset aria-hidden="true">
                                    <legend style={{ width: "0px" }}>
                                      <span className="notranslate">​</span>
                                    </legend>
                                  </fieldset>
                                  <div className="v-text-field__slot">
                                    <div className="v-text-field__prefix">
                                      +880
                                    </div>
                                    {select?.statusPhone ? (
                                      <input
                                        type="text"
                                        inputMode="tel"
                                        placeholder={t("EnterYourPhoneNumber")}
                                        value={mobileInput}
                                        onChange={(e) =>
                                          setMobileInput(e.target.value)
                                        }
                                      />
                                    ) : (
                                      <input
                                        disabled
                                        readOnly
                                        value={
                                          profileData?.phone
                                            ?.toString()
                                            .slice(3) || ""
                                        }
                                        type="text"
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mobile">
                        <div className="row no-gutters justify-space-between">
                          <div className="col col-10">
                            <label
                              className="input-field-label ma-0 text-capitalize d-block"
                              style={{ float: "left" }}
                            >
                              {t("Password")}
                              <span className="red--text ml-1">*</span>
                            </label>
                          </div>
                          <div className="col col-2" />
                          <div className="col col-12">
                            <div
                              className={`v-input input-field elevation-0 hide-details theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined ${
                                phoneError
                                  ? "v-input--has-state error--text"
                                  : ""
                              }`}
                            >
                              <div className="v-input__control">
                                <div className="v-input__slot">
                                  <fieldset aria-hidden="true">
                                    <legend style={{ width: "0px" }}>
                                      <span className="notranslate">​</span>
                                    </legend>
                                  </fieldset>
                                  <div className="v-text-field__slot">
                                    <input
                                      type="text"
                                      placeholder={t("EnterPasswordPlaceholder")}
                                      value={passwordInput}
                                      onChange={(e) =>
                                        setPasswordInput(e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                                {phoneError && (
                                  <div className="v-text-field__details">
                                    <div
                                      className="v-messages theme--light error--text"
                                      role="alert"
                                    >
                                      <div className="v-messages__wrapper">
                                        <div className="v-messages__message">
                                          {phoneError}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row d-flex justify-center align-center no-gutters pt-4">
                        <button
                          type="button"
                          onClick={handleSubmitPhone}
                          disabled={phoneLoading}
                          className={`pa-2 otpSubmitButton v-btn ${
                            phoneLoading ? "v-btn--disabled" : ""
                          } v-btn--has-bg theme--light v-size--default buttonPrimary theme-button theme--light subtitle-1`}
                        >
                          <span className="v-btn__content">{t("Submit")}</span>
                        </button>
                      </div>
                    </>
                  )}

                  {isFieldDialog && (
                    <>
                      <div className="mobile">
                        <div className="row no-gutters justify-space-between">
                          <div className="col col-10">
                            <label
                              className="input-field-label ma-0 text-capitalize d-block"
                              style={{ float: "left" }}
                            >
                              {select?.label}
                            </label>
                          </div>
                          <div className="col col-2" />
                          <div className="col col-12">
                            <div
                              className={`v-input input-field elevation-0 hide-details theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined ${
                                fieldError
                                  ? "v-input--has-state error--text"
                                  : ""
                              }`}
                            >
                              <div className="v-input__control">
                                <div className="v-input__slot">
                                  <fieldset aria-hidden="true">
                                    <legend style={{ width: "0px" }}>
                                      <span className="notranslate">​</span>
                                    </legend>
                                  </fieldset>
                                  <div className="v-text-field__slot">
                                    <input
                                      type={
                                        select?.type === "birthday"
                                          ? "date"
                                          : "text"
                                      }
                                      placeholder={select?.placeholder}
                                      max={
                                        select?.type === "birthday"
                                          ? new Date()
                                              .toISOString()
                                              .split("T")[0]
                                          : undefined
                                      }
                                      value={fieldValue}
                                      onChange={(e) =>
                                        setFieldValue(e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                                {fieldError && (
                                  <div className="v-text-field__details">
                                    <div
                                      className="v-messages theme--light error--text"
                                      role="alert"
                                    >
                                      <div className="v-messages__wrapper">
                                        <div className="v-messages__message">
                                          {fieldError}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={{marginTop:"10px"}} className="row d-flex justify-center align-center no-gutters">
                        <button
                          type="button"
                          onClick={handleSubmitField}
                          disabled={fieldLoading}
                          className={`pa-2 otpSubmitButton v-btn ${
                            fieldLoading ? "v-btn--disabled" : ""
                          } v-btn--has-bg  v-size--default buttonPrimary theme-button  subtitle-1`}
                        >
                          <span className="v-btn__content">{t("Submit")}</span>
                        </button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProfileAccount;
